import { Component, OnInit, Inject, signal } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
  MatDialogClose,
} from '@angular/material/dialog';
import { GenericWarningDialogData } from 'src/app/interfaces';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';

@Component({
  selector: 'app-ride-error',
  templateUrl: './warning.component.html',
  styleUrls: ['./warning.component.scss'],
  imports: [
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    MatDialogActions,
    MatButton,
    MatDialogClose,
    MatIcon,
  ],
})
export class WarningDialog implements OnInit {
  type = signal<string>('');
  text = signal<string>('');
  title = signal<string>('');
  itemName = signal<string>('');
  notification = signal<string>('');
  confirmDialog = signal<boolean>(false);
  denyButtonText = signal<string>('Nee');
  acceptButtonText = signal<string>('Ja');
  confirmButtonValue = signal<any>(true);
  usesHtml = signal<boolean>(false);

  constructor(@Inject(MAT_DIALOG_DATA) public data: GenericWarningDialogData) {}

  getMapsLink(): string {
    const coordinates = this.data.optionalData?.coordinates;
    if (!coordinates) {
      return '';
    }
    const latitude = Number(coordinates.latitude);
    const longitude = Number(coordinates.longitude);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
      return '';
    }
    return `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
  }

  ngOnInit() {
    switch (this.data.dialogType) {
      case 'importFailedRates':
        this.title.set('Importeren mislukt');
        this.text.set('Er kan maar 1 standaard tarief zijn. Pas dit aan en probeer opnieuw.');
        break;

      case 'importFailedCars':
        this.title.set('Importeren mislukt');
        this.text.set(
          `Er mogen maximaal ${this.data.optionalData.maxTotalVehicles} actieve voertuigen zijn. Pas dit aan en probeer opnieuw.`,
        );
        break;

      case 'importPartialSuccess':
        this.title.set('Importeren deels voltooid');
        this.text.set(
          `Niet alle items zijn succesvol geïmporteerd. Mogelijk ontbreekt er een verplicht veld of is er een duplicaat. De overige items zijn wel succesvol geïmporteerd.`,
        );
        break;

      case 'dayPartException':
        this.title.set('Er staat een uitzondering op het gekozen tijdslot');
        this.text.set(
          `Reden: ${this.data.optionalData.exceptionName}${
            this.data.optionalData.exceptionDescription
              ? '\n\r' + this.data.optionalData.exceptionDescription
              : ''
          }\n\n\rWil je de rit toch inplannen?`,
        );
        this.confirmDialog.set(true);
        break;

      case 'uploadSepaMandateIdCheck':
        this.title.set('Controleer machtigingskenmerk');
        this.text.set(
          'Komt het kenmerk van de SEPA machtiging overeen met met het hieronder genoemde machtigingskenmerk?',
        );
        this.confirmDialog.set(true);
        break;

      case 'saveEmptyRide':
        this.title.set('Lege rit opslaan');
        this.text.set('Weet je zeker dat je een lege rit wilt opslaan?');
        this.confirmDialog.set(true);
        break;

      case 'saveEmptyRideOrDelete':
        this.title.set('Lege rit opslaan of verwijderen');
        this.text.set(
          'Deze rit heeft geen passagiers. Wil je de rit opslaan als lege rit of verwijderen?',
        );
        this.confirmDialog.set(true);
        break;

      case 'retourAlreadyBooked':
        this.title.set('Retourrit al bekend');
        this.text.set(
          'Er bestaat al een retourrit voor deze rit.\nWeet je zeker dat je dat deze retourrit wilt inplannen?',
        );
        this.confirmDialog.set(true);
        break;

      case 'vehicleAlreadyBooked':
        this.title.set('Dubbele boeking');
        this.text.set('Er is al een rit voor dit voertuig gepland op dit tijdstip.');
        break;

      case 'userInRide':
        this.title.set('Dubbele boeking');
        this.text.set(
          'Deze passagier is voor dit tijdslot al in een auto geboekt.\nWeet je zeker dat je de passagier aan deze rit wilt toevoegen?',
        );
        this.confirmDialog.set(true);
        break;

      case 'userAlreadyBookedInAnyRide':
        this.title.set('Dubbele boeking');
        this.text.set('Er is voor deze passagier op dit moment al een rit voorbereid of gepland.');
        this.confirmDialog.set(false);
        break;

      case 'driverAlreadyBookedInDaypart':
        this.title.set('Dubbele boeking');
        this.text.set(
          'Deze chauffeur is al ingepland op dit tijdstip en kan niet op meerdere voertuigen tegelijk worden ingepland.',
        );
        this.confirmDialog.set(false);
        break;

      case 'coDriverAlreadyBookedInDaypart':
        this.title.set('Dubbele boeking');
        this.text.set(
          'Deze bijrijder is al ingepland op dit tijdstip en kan niet op meerdere voertuigen tegelijk worden ingepland.',
        );
        this.confirmDialog.set(false);
        break;

      case 'plannerInDaypart':
        this.title.set('Dubbele boeking');
        this.text.set(
          `${this.data.optionalData.plannerName} is voor dit tijdslot al in een dagdeel ingepland.\nWeet je zeker dat je ${this.data.optionalData.plannerName} bij dit dagdeel wilt inplannen?`,
        );
        this.acceptButtonText.set('Ja, toch inplannen');
        this.denyButtonText.set('Nee, niet inplannen');
        this.confirmDialog.set(true);
        break;

      case 'plannerAlreadyBookedInDaypart':
        this.title.set('Dubbele boeking');
        this.text.set(
          `${this.data.optionalData.plannerName} is al ingepland op dit tijdstip en kan niet op meerdere dagdelen tegelijk worden ingepland.`,
        );
        this.confirmDialog.set(false);
        break;

      case 'copyDoubleBooking':
        this.title.set('Dubbele boeking');
        this.text.set(
          `Er zijn 1 of meerdere dubbele boekingen in de reeks. Weet je zeker dat de de rit wil kopiëren?`,
        );
        this.confirmDialog.set(true);
        break;

      case 'noCoDriverSeatsAvailable':
        this.title.set('Geen plek voor een bijrijder');
        this.text.set(
          'Er is geen stoel beschikbaar voor een bijrijder in alle ritten van dit dagdeel.\nAls je toch doorgaat met inroosteren, krijgen deze ritten -1 beschikbare stoel.\nDit moet later handmatig worden hersteld.',
        );
        this.acceptButtonText.set('Ja, toch inroosteren');
        this.denyButtonText.set('Nee, niet inroosteren');
        this.confirmDialog.set(true);
        break;

      case 'userPlanningWarnings': {
        const optData = this.data.optionalData || {};
        const userRole = optData.role || 'Medewerker';
        const uName = optData.userName || 'Deze medewerker';
        const uWarnings = optData.warnings || [];

        this.title.set(`${userRole} niet beschikbaar`);
        const listHtml = `<ul style="margin-top: 10px; margin-bottom: 10px;">${uWarnings
          .map((w) => `<li>${w}</li>`)
          .join(
            '',
          )}</ul><div style="margin-top: 20px;">Weet je zeker dat je de medewerker alsnog wilt inplannen?</div>`;
        this.text.set(`<b>${uName}</b> is niet beschikbaar voor deze dienst.${listHtml}`);
        this.usesHtml.set(true);
        this.acceptButtonText.set('Ja, toch inplannen');
        this.denyButtonText.set('Nee, niet inplannen');
        this.confirmDialog.set(true);
        break;
      }

      case 'standardRate':
        this.title.set('Er bestaat al een standaard tarief');
        this.text.set('Weet je zeker dat je dit tarief het nieuwe standaard tarief wilt maken?');
        this.confirmDialog.set(true);
        break;

      case 'deleteUser':
        this.type.set('Passagier');
        this.title.set('Wil je de passagier verwijderen?');
        this.notification.set(
          this.data.optionalData.userHasCard
            ? 'Let op: de passagier heeft nog een gekoppeld kaartnummer. Deze is niet meer te gebruiken als je de passagier verwijderd zonder het kaartnummer los te koppelen.'
            : '',
        );
        this.itemName.set(this.data.optionalData.userName);
        break;

      case 'deleteVehicleGroups':
        this.type.set('Voertuiggroep');
        this.title.set('Wil je de voertuiggroep verwijderen?');
        this.itemName.set(this.data.optionalData.vehicleGroupName);
        break;

      case 'deleteStandardLocation':
        this.type.set('Adres');
        this.title.set('Wil je het standaard adres verwijderen?');
        this.itemName.set(this.data.optionalData.addressName);
        break;

      case 'deleteDayparts':
        this.type.set('Dagdeel');
        this.title.set('Wil je het dagdeel verwijderen?');
        this.itemName.set(this.data.optionalData.daypartName);
        break;

      case 'deleteException':
        this.type.set('Uitzondering');
        this.title.set('Wil je de uitzondering verwijderen?');
        this.itemName.set(this.data.optionalData.exceptionName);
        break;

      case 'deleteParticularity':
        this.type.set('Bijzonderheid');
        this.title.set('Wil je de bijzonderheid verwijderen?');
        this.itemName.set(this.data.optionalData.particularityName);
        break;

      case 'deleteRecentDestination':
        this.title.set('Wil je het recente adres bij deze passagier verwijderen?');
        this.itemName.set(this.data.optionalData.addressName);
        break;

      case 'deletePhoneNumber':
        this.type.set('Telefoonnummer');
        this.title.set('Wil je het telefoonnummer verwijderen?');
        this.itemName.set(this.data.optionalData.phoneNumber);
        break;

      case 'deleteUnassignedRide':
        this.type.set('Restrit');
        this.title.set('Wil je de restrit verwijderen?');
        break;

      case 'addressNotValid':
        this.title.set('Foutief adres');
        if (
          this.data.optionalData?.failedAddresses &&
          this.data.optionalData.failedAddresses.length > 0
        ) {
          this.text.set('De volgende adressen zijn niet geldig:');
        } else {
          this.text.set(
            'Het ingevulde adres is niet juist. Controleer het volledige adres op (spel)fouten en probeer het opnieuw.',
          );
        }
        this.confirmDialog.set(false);
        break;

      case 'addressNotValidOptional':
        this.title.set('Foutief adres');
        this.text.set(
          'Het ingevulde adres is niet juist. Controleer het volledige adres op (spel)fouten en probeer het opnieuw. Weet je zeker dat je dit adres wilt gebruiken?',
        );

        this.confirmDialog.set(true);
        break;

      case 'addressNotValidUser':
        this.title.set('Adres niet gevonden');
        this.text.set(
          'Onze adrescontrole kon het ingevulde adres niet verifiëren.\n\nControleer het volledige adres op spel- en typefouten. Weet je zeker dat je dit adres wilt gebruiken zoals het nu is ingevuld? Automatisch plannen zal voor deze passagier altijd falen.',
        );
        this.confirmDialog.set(true);
        break;

      case 'addressNotValidUserOptional':
        this.title.set('Adres niet gevonden');
        this.text.set(
          'Onze adrescontrole kon het ingevulde adres niet verifiëren.\n\nControleer het volledige adres op spel- en typefouten. Weet je zeker dat je dit adres wilt gebruiken zoals het nu is ingevuld?',
        );
        this.confirmDialog.set(true);
        break;

      case 'addressLowConfidence': {
        const enteredAddress = this.data.optionalData?.enteredAddress ?? 'Het ingevoerde adres';
        const candidateAddress = this.data.optionalData?.candidateAddress;

        this.title.set('Adres kan niet worden geverifieerd');
        this.text.set(
          `Onze adrescontrole kon het opgegeven adres niet volledig verifiëren. Controleer het gevonden adres en bevestig dat u dit adres wilt gebruiken.<br><br><b>Ingevoerd:</b> ${enteredAddress}${candidateAddress ? `<br><b>Gevonden:</b> ${candidateAddress}` : ''}`,
        );
        this.usesHtml.set(true);
        this.acceptButtonText.set('Ja, adres gebruiken');
        this.denyButtonText.set('Nee, aanpassen');
        this.confirmDialog.set(true);
        break;
      }

      case 'importFailedRows': {
        const importResultText = this.data.optionalData.hasSuccessfulImports
          ? 'De volgende rijen konden niet volledig worden geïmporteerd. Controleer de foutmeldingen en probeer het opnieuw.'
          : 'Geen van de rijen kon worden geïmporteerd. Controleer de foutmeldingen en probeer het opnieuw.';
        const downloadText =
          this.data.optionalData.downloadSucceeded === false
            ? 'De download van de rijen met fouten is mislukt.'
            : 'Het bestand met fouten is gedownload. Corrigeer de gegevens en importeer het opnieuw.';
        this.title.set(
          this.data.optionalData.hasSuccessfulImports
            ? 'Importeren deels voltooid'
            : 'Importeren mislukt',
        );
        this.text.set(`${importResultText}\n\n${downloadText}`);
        this.confirmDialog.set(false);
        break;
      }

      case 'confirmDeactivateImport':
        this.title.set('Passagiersbestand import');
        this.text.set(
          'Het passagiersbestand wordt nog geïmporteerd. Als je de pagina verlaat, wordt de import afgebroken. Weet je zeker dat je de pagina wilt verlaten?',
        );
        this.confirmDialog.set(true);
        this.acceptButtonText.set('Ja, verlaten');
        this.denyButtonText.set('Nee, blijven');
        break;
      case 'movePassengerImmediateSave':
        this.title.set('Passagier verplaatsen');
        this.text.set(
          'Wanneer je deze passagier verplaatst, wordt dit direct opgeslagen. Andere wijzigingen aan de rit worden niet automatisch verwerkt.\nWil je doorgaan?',
        );
        this.confirmDialog.set(true);
        this.acceptButtonText.set('Ja, doorgaan');
        this.denyButtonText.set('Nee, annuleren');
        break;

      case 'movePassengerDone':
        this.title.set('Passagier verplaatsen voltooid');
        this.text.set('De passagier is verplaatst.');
        this.denyButtonText.set('Doorgaan');
        break;

      case 'rideInPast':
        this.title.set('Datum in het verleden');
        this.text.set('Deze datum ligt in het verleden, weet je zeker dat je dit wilt aanpassen?');
        this.confirmDialog.set(true);
        break;

      case 'deactivateVehicle': {
        const includeDriver = this.data.optionalData?.includeDriver === true;
        const includeCoDriver = this.data.optionalData?.includeCoDriver === true;
        const assignmentText = includeDriver
          ? includeCoDriver
            ? 'het voertuig, de chauffeur en de bijrijder'
            : 'het voertuig en de chauffeur'
          : includeCoDriver
            ? 'het voertuig en de bijrijder'
            : 'het voertuig';
        const affectedRideCount = this.data.optionalData?.affectedRideCount ?? 0;
        const rideLabel = affectedRideCount === 1 ? 'rit' : 'ritten';
        const rideDescription =
          affectedRideCount === 1 ? 'De rit zelf blijft' : 'De ritten zelf blijven';

        this.title.set('Voertuig inactief maken?');
        this.text.set(
          `Het voertuig wordt inactief gemaakt. De koppeling met ${assignmentText} wordt verwijderd van <strong>${affectedRideCount} toekomstige ${rideLabel}</strong>. ${rideDescription} bestaan.`,
        );
        this.usesHtml.set(true);
        this.acceptButtonText.set('Ja, inactief maken');
        this.denyButtonText.set('Nee, niet inactief maken');
        this.confirmDialog.set(true);
        break;
      }

      case 'rideCopyBlocked':
        this.title.set('Rit kopiëren niet mogelijk');
        this.text.set(
          this.data.optionalData?.exceptionName === 'missingVehicle'
            ? 'Deze rit heeft geen voertuig.'
            : this.data.optionalData?.exceptionName === 'vehicleInactive'
              ? 'Het voertuig van deze rit is inactief.'
              : 'Het voertuig van deze rit kan niet worden gevonden.',
        );
        break;

      case 'removePassengerFromRide':
        this.title.set('Passagier verwijderen');
        this.text.set(
          `Weet je zeker dat je <b>${this.data.optionalData.passengerName}</b> uit de rit wilt verwijderen?`,
        );
        this.confirmDialog.set(true);
        this.usesHtml.set(true);
        break;

      case 'resetAutomaticRides':
        this.title.set('Automatisch plannen resetten');
        this.text.set(
          `Weet je zeker dat je alle automatisch geplande ritten voor ${this.data.optionalData?.date} wilt resetten? Deze actie kan niet ongedaan worden gemaakt.`,
        );
        this.acceptButtonText.set('Ja, resetten');
        this.denyButtonText.set('Nee, niet resetten');
        this.confirmDialog.set(true);
        break;

      case 'deleteSepaMandate':
        this.title.set('SEPA machtiging verwijderen');
        this.text.set(
          'Weet je zeker dat je de SEPA machtiging van deze passagier wilt verwijderen?',
        );
        this.confirmDialog.set(true);
        break;
    }

    if (this.data.optionalData?.confirmButtonText) {
      this.acceptButtonText.set(this.data.optionalData.confirmButtonText);
    }
    if (this.data.optionalData?.confirmButtonValue) {
      this.confirmButtonValue.set(this.data.optionalData.confirmButtonValue);
    }
    if (this.data.optionalData?.cancelButtonText) {
      this.denyButtonText.set(this.data.optionalData.cancelButtonText);
    }
  }
}
